<span
  class={`${className} ${beforeAfter}-comment`}
  on:click={handleClick}
  on:contextmenu={onContextMenu}
>
  {#if isEditable}
    <span
      bind:this={elementRef}
      class="comment"
      placeholder={$translations[MoveList.Translation.Prompt.AddComment]()}
      on:keydown={handleInput}
      on:focusout={handleFocusOut}
      data-comment-node={node ? `${node.ids.line}-${node.ids.move}` : '0-0'}
      contenteditable="true"
      bind:innerText={commentBuffer}
    />
  {:else}
    <span
      class="comment"
      contenteditable="false"
      data-comment-node={node ? `${node.ids.line}-${node.ids.move}` : '0-0'}
    >
      {commentBuffer}
    </span>
  {/if}
</span>

<script lang="ts">
import { getContext, onMount } from 'svelte';
import { MoveListComponent } from 'components/move-list/config/types';
import { MoveList } from 'chessboard/model/move-list/types';
import { MoveListPlugin } from 'chessboard/plugins/move-list/config/types';
import type { ContextShape } from '../store/types';

export let comment: MoveListComponent.Comment;
const node = comment.node;
export let className: string = '';

const { store, eventHandlers } = getContext<ContextShape>(MoveListPlugin.Name);
const { isActive, translations, commentsEditable } = store;
$: isEditable = $isActive && $commentsEditable;

const beforeAfter = comment.isBeforeComment ? 'before' : 'after';

let elementRef: HTMLElement;
let saveTimer: number | undefined;

let originalCommentText: string = comment.comment ?? '';
let commentBuffer: string = originalCommentText;

$: if (comment.comment !== originalCommentText) {
  originalCommentText = comment.comment;
  commentBuffer = originalCommentText;
}

onMount(() => {
  if (commentBuffer === '') {
    elementRef.focus();
  }
  return () => {
    // Prevents debounced save after component is destroyed (e.g. on blur after comment is deleted)
    clearTimeout(saveTimer);
  };
});

/**
 * on click handler for the comment node
 */
function handleClick() {
  if (node) {
    eventHandlers.clickComment(node.ids);
  }
  elementRef.focus();
}

/**
 * on key press handler for the comment node
 */
function handleInput(event: KeyboardEvent) {
  const enterPressed = event.code === 'Enter' || event.code === 'NumpadEnter';
  if (enterPressed) {
    elementRef.blur();
    event.preventDefault();
    return;
  }
  clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    if (elementRef.innerText !== originalCommentText) {
      eventHandlers.updateComment(comment, elementRef.innerText);
    }
  }, MoveListComponent.Constants.CommentUpdateDelay);
}

/**
 * on context menu handler for the comment node
 */
function onContextMenu(ev: MouseEvent) {
  if (node) {
    eventHandlers.openContextMenu(node, ev);
  }
}

/**
 * on focus out handler for the comment node
 */
function handleFocusOut(_event: FocusEvent) {
  if (commentBuffer === '') {
    eventHandlers.updateComment(comment, undefined);
  }
}
</script>

<style src="./comment.scss"></style>
